import React, { useEffect, useState } from "react";
import api from "../api";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { UserPlus, UserCheck, Trash2, CalendarClock } from "lucide-react";

const fmt = (iso) => { try { return format(new Date(iso), "dd MMM yyyy", { locale: it }); } catch { return iso; } };

const STATUS_CHIP = {
  attiva: { label: "Attiva", cls: "bg-emerald-100 text-emerald-700" },
  programmata: { label: "Programmata", cls: "bg-amber-100 text-amber-700" },
  scaduta: { label: "Scaduta", cls: "bg-gray-100 text-gray-500" },
};

export const DelegationsManager = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ given: [], received: [] });
  const [cands, setCands] = useState([]);
  const [form, setForm] = useState({ to_id: "", start_date: "", end_date: "" });

  const load = () => { api.get("/delegations").then((r) => setData(r.data)).catch(() => {}); };
  useEffect(() => {
    load();
    api.get("/delegations/candidates").then((r) => setCands(r.data)).catch(() => {});
  }, []);

  const activeReceived = (data.received || []).filter((d) => d.status === "attiva");
  const givenActive = (data.given || []).filter((d) => d.status !== "scaduta");

  const create = async () => {
    if (!form.to_id || !form.start_date || !form.end_date) { toast.error("Compila collega e periodo"); return; }
    try {
      await api.post("/delegations", form);
      toast.success("Delega creata");
      setForm({ to_id: "", start_date: "", end_date: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nella creazione della delega"); }
  };

  const revoke = async (id) => {
    try { await api.delete(`/delegations/${id}`); toast.success("Delega revocata"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="mb-6" data-testid="delegations-manager">
      {activeReceived.length > 0 && (
        <div className="mb-4 rounded-xl border border-[#ff7e00]/30 bg-[#fff6ee] px-4 py-3 flex items-start gap-3" data-testid="delegation-received-banner">
          <UserCheck size={18} className="text-[#ff7e00] shrink-0 mt-0.5" />
          <div className="text-[13.5px] text-[#8a4b00]">
            Stai gestendo le approvazioni per conto di{" "}
            <b>{activeReceived.map((d) => d.from_name).join(", ")}</b>{" "}
            (fino al {fmt(activeReceived.map((d) => d.end_date).sort().slice(-1)[0])}).
          </div>
        </div>
      )}
      <div className="flex justify-end">
        <button onClick={() => setOpen(true)} data-testid="delegations-open-btn"
          className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#16345c] border border-[#16345c]/20 hover:border-[#16345c]/50 hover:bg-[#16345c]/5 px-3 py-2 rounded-lg transition-colors">
          <CalendarClock size={15} /> Gestisci deleghe
          {givenActive.length > 0 && <span className="ml-1 text-[11px] font-bold bg-[#ff7e00] text-white px-1.5 py-0.5 rounded-full">{givenActive.length}</span>}
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="delegations-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus size={18} /> Delega approvazioni</DialogTitle></DialogHeader>
          <p className="text-[13px] text-gray-500 -mt-1">Affida temporaneamente a un collega l'approvazione delle richieste dei tuoi reparti (es. durante le tue ferie). Puoi revocarla in qualsiasi momento.</p>

          <div className="grid gap-3 mt-2">
            <div>
              <label className="block text-[12px] font-semibold text-gray-500 mb-1">Delega a</label>
              <Select value={form.to_id} onValueChange={(v) => setForm((f) => ({ ...f, to_id: v }))}>
                <SelectTrigger data-testid="delegation-colleague"><SelectValue placeholder="Scegli un collega" /></SelectTrigger>
                <SelectContent>
                  {cands.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.department ? ` · ${c.department}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[12px] font-semibold text-gray-500 mb-1">Dal</label>
                <input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                  data-testid="delegation-start" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-[14px]" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-gray-500 mb-1">Al</label>
                <input type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                  data-testid="delegation-end" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-[14px]" />
              </div>
            </div>
            <button onClick={create} data-testid="delegation-create-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#ff7e00] hover:bg-[#e56f00] text-white text-[14px] font-bold px-4 py-2.5 rounded-lg transition-colors">
              <UserPlus size={16} /> Crea delega
            </button>
          </div>

          <div className="mt-4">
            <h4 className="text-[12px] font-black uppercase tracking-widest text-gray-400 mb-2">Le mie deleghe</h4>
            {(data.given || []).length === 0 ? (
              <div className="text-[13px] text-gray-400 py-2" data-testid="delegations-empty">Nessuna delega creata.</div>
            ) : (
              <div className="space-y-2">
                {data.given.map((d) => {
                  const chip = STATUS_CHIP[d.status] || STATUS_CHIP.scaduta;
                  return (
                    <div key={d.id} data-testid={`delegation-row-${d.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2">
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-semibold text-[#16345c] truncate">{d.to_name}
                          <span className={`ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${chip.cls}`}>{chip.label}</span>
                        </div>
                        <div className="text-[12px] text-gray-500">{fmt(d.start_date)} → {fmt(d.end_date)}</div>
                      </div>
                      <button onClick={() => revoke(d.id)} data-testid={`delegation-revoke-${d.id}`}
                        className="p-1.5 rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50 shrink-0" title="Revoca delega"><Trash2 size={16} /></button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DelegationsManager;
